import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@design-lab/shared/graphql";
import { useAtom } from "jotai";
import { shopifyRecipeLoadingAtom } from "@design-lab/shared/state";
import { useNotification } from "@design-lab/shared/ui/notification";
import { trackDesignLabCtaClicked } from "@design-lab/shared/lib/analytics";
import { useDesignProjectSync } from "@design-lab/features/design-project";
import { CreateShopifyRecipeDocument } from "graphql-api/operations";
import type { CreateShopifyRecipeInput } from "graphql-api/types";

const REDIRECT_DELAY_MS = 3000;

/**
 * Hook for creating Shopify recipes from the current design project.
 *
 * Manages the mutation state, loading indicator, and user notifications
 * for the recipe creation flow.
 *
 * @returns Object containing:
 *   - createShopifyRecipe: Function to trigger recipe creation (requires storeId)
 *   - isLoading: Whether the mutation is in progress
 */
export function useCreateShopifyRecipe() {
  const [createRecipeMutation] = useMutation(CreateShopifyRecipeDocument);
  const [isLoading, setIsLoading] = useAtom(shopifyRecipeLoadingAtom);
  const { success, error } = useNotification();
  const { projectUuid } = useDesignProjectSync();
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    },
    []
  );

  /**
   * Creates Shopify recipes from the current design project.
   *
   * @param shopifyStoreId - The ID of the Shopify store (required)
   * @returns Object with success status and recipe IDs if successful
   */
  const createShopifyRecipe = useCallback(
    async (shopifyStoreId: string) => {
      if (!projectUuid) {
        error({
          title: "Error",
          description: "Please save your project first before creating a Shopify recipe."
        });
        return { success: false };
      }

      if (!shopifyStoreId) {
        error({
          title: "Error",
          description: "Please select a Shopify store."
        });
        return { success: false };
      }

      trackDesignLabCtaClicked("create_shopify_recipe");
      setIsLoading(true);

      try {
        const input: CreateShopifyRecipeInput = {
          designProjectUuid: projectUuid,
          shopifyStoreId
        };

        const result = await createRecipeMutation({
          variables: { input }
        });

        const response = result.data?.createShopifyRecipe;

        if (response?.success) {
          success({
            title: "Success",
            description: "Shopify recipe created successfully! Redirecting to new design..."
          });
          redirectTimeoutRef.current = setTimeout(() => {
            window.location.href = "/design-lab";
          }, REDIRECT_DELAY_MS);
          return { success: true, recipeIds: response.recipeIds };
        }
        error({
          title: "Failed to Create Recipe",
          description: response?.errorMessage || "An unexpected error occurred."
        });
        return { success: false };
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred.";
        error({
          title: "Error",
          description: errorMessage
        });
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [createRecipeMutation, projectUuid, setIsLoading, success, error]
  );

  return {
    createShopifyRecipe,
    isLoading
  };
}
